import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from 'react-query';
import { axiosClient, url } from '@/api';
import { AxiosError } from 'axios';
import { AgeGroup, ErrorObject, KycStatus } from '../common.types';

export interface KycCountry {
  iso2: string;
  iso3: string;
  callingCode: string;
  name: string;
}

export type KycVerificationType = 'REGISTRATION' | 'BUY_CRYPTO';

// Mirrors backend io.relaypay.api.kyc.dto.KycFailureReason (RNB-195).
export type KycFailureReason =
  | 'NONE'
  | 'RESUBMISSION_REQUESTED'
  | 'FINAL_REJECTION'
  | 'DUPLICATE_ACCOUNT';

export interface KycStatusResponse {
  status: KycStatus;
  ageGroup: AgeGroup;
  // RNB-195: whether the user can retry verification, and why the last review failed.
  canRetry?: boolean;
  failureReason?: KycFailureReason | null;
  failureMessage?: string | null;
  rejectLabels?: string[] | null;
}

export interface AccessTokenPayload {
  verificationType: KycVerificationType;
  verificationId: string;
}

export function useCheckStatus<
  TSelectData = KycStatusResponse,
  TError = AxiosError<ErrorObject>,
>(
  options?: Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['CheckStatus'],
    () => axiosClient.get(url.kycController.checkStatus),
    options,
  );
}

export function useGetAccessToken<TContext>(
  options?: Omit<
    UseMutationOptions<{ token: string }, AxiosError<ErrorObject>, AccessTokenPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<{ token: string }, AxiosError<ErrorObject>, AccessTokenPayload, TContext> {
  const key = ['GetAccessToken'];

  return useMutation((body: AccessTokenPayload) => axiosClient.post(url.kycController.generateAccessToken, body), {
    ...options,
    mutationKey: key,
  });
}

export interface InitiateWithdrawCheckResponse {
  verificationId: string;
}

export function useInitiateWithdrawCheck<TContext>(
  options?: Omit<
    UseMutationOptions<InitiateWithdrawCheckResponse, AxiosError<ErrorObject>, void, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<InitiateWithdrawCheckResponse, AxiosError<ErrorObject>, void, TContext> {
  return useMutation(
    () => axiosClient.post(url.kycController.initiateWithdrawCheck),
    {
      ...options,
      mutationKey: ['InitiateWithdrawCheck'],
    },
  );
}

export function useGetCountries<TSelectData = KycCountry[], TError = AxiosError<ErrorObject>>(
  options?: Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['GetCountries'],
    () => axiosClient.get(url.kycController.getCountries),
    options,
  );
}
